/*
 * Parcel protection toggle for the cart (C02).
 *
 * Markup, tier table and the "already in sync?" check are rendered by
 * snippets/parcel-protection.liquid inside the cart line items. Broadcast's
 * <cart-items> replaces that HTML after every cart change, so this element is
 * re-created each time: per-element listeners live and die with it, and only
 * the small `state` object below is shared.
 *
 * Every change is decided from a fresh /cart.js response (never from HTML that
 * might be stale) and then handed back to Broadcast via `theme:cart:refresh`.
 */
(() => {
  if (customElements.get('parcel-protection')) return;

  const ATTRIBUTE = '_parcel_protection';
  const ACCEPTED = 'accepted';
  const DECLINED = 'declined';

  const state = {
    busy: false, // one parcel protection request at a time
    focus: null, // element (or the switch) to refocus once the cart has re-rendered
    lastPlan: null, // last automatic plan tried; stops retry loops if the cart refuses it
  };

  const jsonHeaders = {'Content-Type': 'application/json', Accept: 'application/json'};

  async function request(url, body) {
    const options = body ? {method: 'POST', headers: jsonHeaders, body: JSON.stringify(body)} : {headers: {Accept: 'application/json'}};
    const response = await fetch(url, options);
    const data = await response.json().catch(() => null);

    if (!response.ok || !data || typeof data.status === 'number') {
      throw new Error(data?.description || data?.message || `Request failed with status ${response.status}`);
    }

    return data;
  }

  /**
   * Work out what the cart needs, from live cart data.
   * Returns null when protection is wanted but its tier variant is missing or unavailable.
   */
  function planFor(cart, config, preference) {
    const isProtection = (item) => item.product_id === config.productId;
    const lines = cart.items.filter(isProtection);
    const merchandise = cart.items.filter((item) => !isProtection(item)).reduce((sum, item) => sum + item.final_line_price, 0);

    if (preference === undefined) {
      const stored = cart.attributes?.[ATTRIBUTE];
      preference = stored === ACCEPTED || (stored !== DECLINED && config.defaultOn);
    }

    const wanted = preference && merchandise > 0;
    const tier = config.tiers.find(({max}) => max === null || merchandise <= max);
    const target = wanted ? tier?.variant : null;

    if (wanted && !target) return null;

    const keep = target ? lines.find((line) => line.variant_id === target) : null;
    const updates = {};
    const removed = [];

    lines.forEach((line) => {
      if (line !== keep) {
        updates[line.key] = 0;
        removed.push(line.variant_id);
      }
    });

    if (keep && keep.quantity !== 1) updates[keep.key] = 1;

    const add = target && !keep ? target : null;
    // On a tier change, the variant to put back if adding the new one fails.
    const restore = add && lines.length ? lines[0].variant_id : null;
    // Identifies the change by variants, not line keys: a restored line gets a new key.
    const signature = JSON.stringify([add, removed.sort(), Boolean(keep && keep.quantity !== 1)]);

    return {add, restore, updates, signature};
  }

  const isEmptyPlan = (plan) => !plan.add && !Object.keys(plan.updates).length;

  const SWITCH = 'switch';

  // Runs on `theme:product:added`, which Broadcast dispatches once a re-render has
  // finished and its cart controls are enabled again.
  function restoreFocus() {
    const target = state.focus === SWITCH ? document.querySelector('parcel-protection [data-parcel-protection-input]') : state.focus;
    state.focus = null;

    if (target?.isConnected && !target.disabled) target.focus({preventScroll: true});
  }

  /**
   * Remove before adding so the cart never holds two protection lines. If the add
   * fails, the previous tier is put back, and a new preference is only saved once
   * the cart actually matches it.
   */
  async function applyPlan(plan, attributes) {
    const {routes} = window.theme;
    const update = (body) => request(`${routes.cart_update_url}.js`, body);
    const add = (id) => request(`${routes.cart_add_url}.js`, {items: [{id, quantity: 1}]});
    const hasUpdates = Object.keys(plan.updates).length > 0;

    if (!plan.add) {
      if (hasUpdates || attributes) await update({updates: plan.updates, attributes});
      return;
    }

    if (hasUpdates) await update({updates: plan.updates});

    try {
      await add(plan.add);
    } catch (error) {
      if (plan.restore) await add(plan.restore).catch(() => {});
      throw error;
    }

    if (attributes) await update({updates: {}, attributes});
  }

  class ParcelProtection extends HTMLElement {
    constructor() {
      super();

      this.onChange = this.onChange.bind(this);
      this.sync = this.sync.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector('[data-parcel-protection-input]');
      this.cartItems = this.closest('cart-items');
      this.drawer = this.closest('cart-drawer');

      this.input?.addEventListener('change', this.onChange);
      this.cartItems?.removeAttribute('aria-busy');

      if (this.dataset.inSync === 'true') {
        state.lastPlan = null;
        return;
      }

      // Don't pop the drawer open for a background change; wait until the customer opens it.
      if (this.drawer && !this.drawer.cartDrawerIsOpen) {
        document.addEventListener('theme:cart-drawer:open', this.sync, {once: true});
      } else {
        // This runs inside Broadcast's build(), which re-enables the cart controls when it
        // finishes; start once it has, so the lock taken by sync() is not undone.
        queueMicrotask(this.sync);
      }
    }

    disconnectedCallback() {
      this.input?.removeEventListener('change', this.onChange);
      document.removeEventListener('theme:cart-drawer:open', this.sync);
    }

    get config() {
      return {
        productId: Number(this.dataset.productId),
        defaultOn: this.dataset.default !== 'disabled',
        tiers: JSON.parse(this.dataset.tiers || '[]'),
      };
    }

    /**
     * Keep the protection line matching the preference and the merchandise tier
     * after any other cart change.
     */
    async sync() {
      if (state.busy || !this.isConnected) return;

      state.busy = true;
      this.lock();
      let changed = false;

      try {
        const cart = await request(`${window.theme.routes.cart_url}.js`);
        const plan = planFor(cart, this.config);

        if (!plan) {
          console.warn('Parcel protection: no available variant for this cart value, so the cart was left unchanged.');
        } else if (plan.signature === state.lastPlan) {
          if (!isEmptyPlan(plan)) console.warn('Parcel protection: the cart did not accept the last change, so it was not retried.');
        } else {
          state.lastPlan = plan.signature;
          // Re-render even for an empty plan: the row that asked for this sync was stale.
          changed = true;
          if (!isEmptyPlan(plan)) await applyPlan(plan);
        }
      } catch (error) {
        console.warn('Parcel protection: could not update the cart.', error);
      } finally {
        state.busy = false;
        changed ? this.refresh() : this.unlock();
      }
    }

    async onChange() {
      const enabled = this.input.checked;

      if (state.busy) {
        this.input.checked = !enabled;
        return;
      }

      state.busy = true;
      this.lock();
      let changed = false;

      try {
        const cart = await request(`${window.theme.routes.cart_url}.js`);
        const plan = planFor(cart, this.config, enabled);

        if (!plan) throw new Error('No available variant for this cart value.');

        changed = true;
        await applyPlan(plan, {[ATTRIBUTE]: enabled ? ACCEPTED : DECLINED});
        state.lastPlan = null;
      } catch (error) {
        console.warn('Parcel protection: could not update the cart.', error);
        this.input.checked = !enabled;
      } finally {
        state.busy = false;
        changed ? this.refresh() : this.unlock();
      }
    }

    /**
     * Broadcast's own loading state: top progress bar, hidden total, disabled cart controls.
     * Taken before the first request, because Broadcast addresses cart lines by position
     * and a protection change would shift those positions under a pending quantity update.
     * Disabling the controls drops focus, so it is put back after the re-render.
     */
    lock() {
      const active = document.activeElement;

      if (this.cartItems?.cart?.contains(active)) {
        state.focus = this.contains(active) ? SWITCH : active;
      } else if (this.drawer?.cartDrawerIsOpen) {
        // The drawer is still opening: Broadcast would focus its close button, which is disabled for now.
        state.focus = this.drawer.querySelector('[data-cart-drawer-close]');
      }

      if (state.focus) document.addEventListener('theme:product:added', restoreFocus, {once: true});

      this.cartItems?.cart?.classList.add('is-loading');
      this.cartItems?.disableCartButtons?.();
      this.cartItems?.setAttribute('aria-busy', 'true');
    }

    // Release the lock when nothing changed, so no re-render is needed.
    unlock() {
      document.removeEventListener('theme:product:added', restoreFocus);
      this.cartItems?.enableCartButtons?.();
      this.cartItems?.removeAttribute('aria-busy');
      restoreFocus();
    }

    // Broadcast re-renders the cart (and clears the loading state) on this event.
    refresh() {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
    }
  }

  customElements.define('parcel-protection', ParcelProtection);
})();
